/* This file contains some basic utility functions used in many places across app */

import { HttpParams } from "@angular/common/http";
import { Primitive } from "@sentry/types";
import { environment } from 'environments/environment';
import { Buffer } from 'buffer';

// TURN BASE64 ENCODED STRING INTO BLOB
export function b64toBlob(b64Data, contentType = '', sliceSize = 512): Blob {
	const byteCharacters = atob(b64Data);
	const byteArrays = [];

	for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
		const slice = byteCharacters.slice(offset, offset + sliceSize);

		const byteNumbers = new Array(slice.length);
		for (let i = 0; i < slice.length; i++) {
			byteNumbers[i] = slice.charCodeAt(i);
		}

		const byteArray = new Uint8Array(byteNumbers);

		byteArrays.push(byteArray);
	}

	const blob = new Blob(byteArrays, { type: contentType });
	return blob;
}

// CONVERT BLOB TO FILE
export function blobToFile(blob: Blob, fileName: string): File {
	const b: any = blob;
	b.lastModifiedDate = new Date();
	b.name = fileName;
	return blob as File;
}

const imageMimeTypeExtensionMap: Record<string, string> = {
	'image/avif': 'avif',
	'image/gif': 'gif',
	'image/jpeg': 'jpeg',
	'image/jpg': 'jpg',
	'image/png': 'png',
	'image/svg+xml': 'svg',
	'image/webp': 'webp',
};

export function getImageExtensionFromMimeType(mimeType: string): string {
	const normalizedMimeType = mimeType?.toLowerCase().split(';')[0];
	if (imageMimeTypeExtensionMap[normalizedMimeType]) return imageMimeTypeExtensionMap[normalizedMimeType];

	const fallbackExtension = normalizedMimeType?.split('/')[1]?.replace('svg+xml', 'svg')?.replace(/[^a-z0-9]/g, '');
	return fallbackExtension || 'jpeg';
}

export function blobToImageFile(blob: Blob, fileNameWithoutExtension = 'profile-image'): File {
	return blobToFile(blob, `${fileNameWithoutExtension}.${getImageExtensionFromMimeType(blob.type)}`);
}

// ENCODE UNICODE STRING IN BASE64
export function b64EncodeUnicode(str: string): string {
	// first we use encodeURIComponent to get percent-encoded UTF-8,
	// then we convert the percent encodings into raw bytes which
	// can be fed into btoa.
	return btoa(
		encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, function toSolidBytes(
			match,
			p1
		) {
			return String.fromCharCode(parseInt('0x' + p1));
		})
	);
}

// LINKIFY CHAT MESSAGES
export function linkify(message: string, isSender: boolean): string {
	if (!message || typeof message !== 'string') return '';
	return message
	.split('\n')
    .map(line => {
        return line
            .split(/\s+/)
            .map(word => {
                if (word.match(/^https?:\/\//)) {
                    return `<a href="${word}" target="_blank" rel="noopener noreferrer" class="is-underlined ${isSender ? 'has-text-color-primary-complement' : 'has-text-color-content'}">${word}</a>`;
                } else if (word.match(/^www\./)) {
                    return `<a href="https://${word}" target="_blank" rel="noopener noreferrer" class="is-underlined ${isSender ? 'has-text-color-primary-complement' : 'has-text-color-content'}">${word}</a>`;
                } else {
                    return word;
                }
            })
            .join(' '); 
        })
    .join('\n');
}

// DETECT STREAM PROVIDER
export function detectProvider(url: string): string {
	if (url.indexOf('youtube') !== -1 || url.indexOf('youtu.be') !== -1) return 'youtube';
	if (url.indexOf('player.vimeo') !== -1) return 'vimeo';
	if (url.indexOf('vimeo') !== -1) return 'vimeo-embed';
	if (url.indexOf('glisser') !== -1 || url.indexOf('glsr') !== -1) return 'glisser';
	if (url.indexOf('sli.do') !== -1) return 'slido';
	if (url.indexOf('zoom.us') !== -1) return 'zoom';
	return '';
}

// ADD SLIDO PARAMETERS TO STREAM URL
export function addSlidoParams(url: string, user: any): string {
	// if slido add user info as query params
	url = url + (url.indexOf('?') !== -1 ? '&' : '?');
	url =
		url +
		`user_name=${encodeURIComponent(
			user.name.split(' ').join('')
		)}&company_name=${encodeURIComponent(
			user.company_name ? user.company_name.split(' ').join('') : 'Grip'
		)}`;
	return url;
}

// DETECT INTERNET EXPLORER
export function browserIsInternetExplorer(): boolean {
	const ua = (window as any).navigator.userAgent; // Check the userAgent property of the window.navigator object
	if (!ua) return false;
	const msie = ua.indexOf('MSIE '); // IE 10 or older
	const trident = ua.indexOf('Trident/'); // IE 11
	if (msie !== -1 || trident !== -1) return true;
	return false;
}

// FORMAT DATA EXPORTS - exceljs
export function formatDataExport(
	workbook: any,
	title: string,
	exportData: any[],
	narrowRows: number[]
): any {
	const worksheet = workbook.addWorksheet(title);

	// set columns with header info and general table style
	// @ts-ignore
	worksheet.columns = Object.keys(exportData[0]).map((th, i) => {
		return {
			header: th
				.split('_')
				.map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
				.join(' '),
			key: th,
			width: narrowRows.includes(i) ? 15 : 30,
			style: {
				font: {
					name: 'Helvetica Neue'
				}
			}
		};
	});

	// set row data
	const xData = exportData.map(row => {
		return Object.values(row);
	});
	worksheet.addRows(xData, 'n');

	// style header row
	worksheet.getRow(1).font = {
		name: 'Helvetica Neue',
		size: 11,
		bold: true
	};
	worksheet.getRow(1).border = {
		top: {
			style: 'thin'
		},
		left: {
			style: 'thin'
		},
		bottom: {
			style: 'thin'
		},
		right: {
			style: 'thin'
		}
	};
	worksheet.getRow(1).fill = {
		type: 'pattern',
		pattern: 'solid',
		fgColor: {
			argb: 'FFf7f4fe'
		}
	};

	return workbook;
}

// SUBSTITUTE NAME <NAME>
export function subStringName(trans: string, name: string): string {
	return trans.replace('<NAME>', name);
}

// SUBSTITUTE NAME [name]
export function subStringNameSq(trans: string, name: string): string {
	return trans.replace('[name]', name);
}

// CAPITALIZE FIRST LETTER
export function uppercaseString(value: string): string {
	if (value && value.length) {
		return value.charAt(0).toUpperCase() + value.slice(1);
	} else {
		return '';
	}
}

// SHORTEN SUMMARY
export function substringListSummary(summary: string): string {
	if (!summary) {
		return '';
	}
	if (summary.length > 75) {
		return summary.substring(0, 75).trim() + '...';
	} else {
		return summary;
	}
}

// SET MESSAGE COUNT
export function setMessageCount(count: number, str: string): string {
	if (!str) return '';
	if (count === 1) {
		str = str.replace('[messages]', 'message');
	} else {
		str = str.replace('[messages]', 'messages');
	}
	str = str.replace('[count]', count.toString());
	return str;
}

// GET HYBRID GROUP ACCESS
export function getHybridGroupAccess(
	groups: any[],
	defaultAccess: string
): string {
	// loop through groups to find user access type
	// ignore speed networking created groups and private session groups
	const nonSpeedNetworkingGroupsAccess: any[] = groups
		?.filter(
			group =>
				group.is_speed_networking_group === 0
				&& group.access
		)
		?.map(group => group.access);

	if (nonSpeedNetworkingGroupsAccess?.length) {
		// prioritise hybrid if user member of virtual AND hybrid groups
		return nonSpeedNetworkingGroupsAccess.includes('hybrid')
			? 'hybrid'
			: nonSpeedNetworkingGroupsAccess[0];
	} else {
		// otherwise return defaultAccess if available or 'virtual'
		return defaultAccess ?? 'virtual';
	}
}

// SEND GOOGLE ANALYTICS EVENT
export function sendGoogleAnalyticsEvent(
	category: string,
	action: string
): void {
	/*
		DEBUGGING:
			Ensure that cookies have been accepted
			If debugging on your local machine and after making changes,
			rather than expecting it to work on a refresh, you must stop and restart the local server.
	*/
	if (typeof (window as any)?.ga === 'function') {
		(window as any).ga('send', 'event', {
			eventCategory: category,
			eventAction: action
		});
	}
}

export function getUnixTimestamp(date: Date): number {
	return Math.floor(date.getTime() / 1000);
}

export function getEnumValues<T>(anEnum: T): T[keyof T][] {
	return Object.keys(anEnum) as unknown as T[keyof T][];
}

export function getRandomValueFromEnum<T>(anEnum: T): T[keyof T] {
	const enumValues: T[keyof T][] = getEnumValues(anEnum);

	const randomIndex: number = Math.floor(Math.random() * enumValues.length);
	const randomEnumValue: T[keyof T] = enumValues[randomIndex];

	return randomEnumValue;
}

export function returnPositiveNumberOrZero(numberToCheck: number): number {
	return numberToCheck > 0
		? numberToCheck
		: 0;
}

export function copyStringToClipboard(text: string): Promise<any> {
	return navigator.clipboard.writeText(text);
}

export function buildUrlParams<T extends Record<string, any>>(paramsMap: T): HttpParams {
	let httpParams = new HttpParams();

	Object.keys(paramsMap)
		.forEach(
			(key: string) => {
				if (paramsMap[key]) {
					if (Array.isArray(paramsMap[key])) {
						paramsMap[key].forEach(
							(value: Primitive) => httpParams = httpParams.append(key, String(value))
						);
					}
					else {
						httpParams = httpParams.append(key, String(paramsMap[key]));
					}

				}
			}
		);

	return httpParams;
}

/**
 * Return uniq filtered string with a given function for generating custom key
 * @param a input array
 * @param key function for returning a key
 * @returns filtered array
 */
export function uniqBy(a: any[], key: Function): any[] {
	let seen = {};
	return a.filter(function (item) {
		let k = key(item);
		return seen.hasOwnProperty(k) ? false : (seen[k] = true);
	});
}


interface PositionalMeta {
	page?: number;
	list_index?: number;
};

// adds page and position data
// initially for analysis of search result quality/relevance (GW: May23)
export function generatePositionalMeta(pos: number, pageSize: number): PositionalMeta {
	if (!Number.isInteger(pos) || pos < 0 || !Number.isInteger(pageSize) || pageSize < 0) return {};
	return {
		page: Math.floor(pos / pageSize) + 1,
		list_index: pos,
	};
}

// use our dynamic image resizer to generate a url using key (path to image) and edits (config)
// https://introsat.atlassian.net/wiki/spaces/ID/pages/1159626770/Dynamic+Image+Resizer
export function generateDynamicResizedImageUrl(key: string, edits: Record<string, any>): string {
	const imageConfig = {
		bucket: environment.imagesBucket,
		key,
		edits
	};
	const imageRequest = JSON.stringify(imageConfig);
	return `${environment.cloudFrontUrl}image/${Buffer.from(imageRequest).toString('base64')}`;
}

export function hexToRgbObj(hex: string): { r: number; g: number; b: number } | null {
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	return result
		? {
			r: parseInt(result[1], 16),
			g: parseInt(result[2], 16),
			b: parseInt(result[3], 16)
		}
		: null;
}
